import { useState, useMemo } from "react";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/react-table";
import {
  ArticleType,
  type PatternSymbolColor,
  type TemplateNodeType,
} from "../../../../../__generated__/gql/graphql";
import { Search } from "../../../../../ui/Icon/Icon";
import Table from "../../../../../ui/Table/Table";
import type { FragmentType } from "../../../../../__generated__/gql";
import { getFragmentData, graphql } from "../../../../../__generated__/gql";
import styles from "./KeywordTable.module.scss";
import TextInput from "../../../../../ui/TextInput/TextInput";
import formatArticleType from "../../../../../util/format-article-type";

interface SymbolTemplate {
  type: TemplateNodeType.Symbol;
  name: string;
  value: string;
  color: PatternSymbolColor;
}

interface TextTemplate {
  type: TemplateNodeType.Text;
  value: string;
}

export type KeywordTableColumnTemplate = SymbolTemplate | TextTemplate;

interface KeywordTableColumn {
  id: string;
  title?: string | null;
  type?: ArticleType | null;
}

const ArticleFragment = graphql(/* GraphQL */ `
  fragment PatternTable_Article on Article {
    id
    title
    type
    keyword
    status
    createdAt
  }
`);

function PatternCell({ row }: CellContext<KeywordTableColumn, unknown>) {
  return (
    <div className={styles.articleCell}>
      <span className={styles.articleTitle}>{row.original.title}</span>
      <span className={styles.articleType}>
        {row.original.type ? formatArticleType(row.original.type) : "-"}
      </span>
    </div>
  );
}

function KeywordTable({
  articles: articlesProp,
  setSelectedArticles,
}: {
  articles: FragmentType<typeof ArticleFragment>[];
  setSelectedArticles: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const [keyphrase, setKeyphrase] = useState<string>("");

  const articles = getFragmentData(ArticleFragment, articlesProp);

  const filteredArticles = useMemo(() => {
    return articles.filter((article) =>
      article.title?.toLowerCase().includes(keyphrase.toLowerCase()),
    );
  }, [articles, keyphrase]);

  const columnHelper = createColumnHelper<KeywordTableColumn>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<KeywordTableColumn, any>[] = [
    columnHelper.display({
      header: "Article",
      cell: PatternCell,
    }),
  ];

  return (
    <div className={styles.container}>
      <div className={styles.tableContainer}>
        <Table
          Header={
            <div className={styles.header}>
              <TextInput
                leftSection={<Search size="medium" />}
                onChange={(event) => {
                  setKeyphrase(event.currentTarget.value);
                }}
                placeholder="Search articles"
                className={styles.search}
                value={keyphrase}
              />
            </div>
          }
          clearSelectionOnChange={false}
          columns={columns}
          data={filteredArticles}
          defaultRowSelection={articles.reduce(
            (acc, article) => ({ ...acc, [article.id]: true }),
            {},
          )}
          isSelectable
          onRowSelectionChange={(newRowSelection) => {
            const newSelectedArticles = Object.entries(newRowSelection)
              .filter(([, v]) => v)
              .map(([id]) => id);
            setSelectedArticles(newSelectedArticles ?? []);
          }}
          showSelectionHeader={false}
          totalCount={articles.length ?? 0}
        />
      </div>
    </div>
  );
}
export default KeywordTable;
